'use client';
import { useState, useRef, Dispatch, SetStateAction, useCallback } from 'react';
import cx from 'classnames';
import styles from './Dropdown.module.scss';
import Icon from '../Icon';
import RadioItem, { OptionType } from './RadioItem/RadioItem';
import useEscapeHandlers from '../../util/useEscapeHandlers';
import { kebabCase } from '../../util/stringUtils';
import { Size, Variant } from './types';

export type DropdownProps = {
  /**
   * Sets className for fieldset of options with additional CSS styles
   */
  classFieldset?: string;
  /**
  /**
   * Sets the title/label of the dropdown if one is required
   */
  title?: string;
  /**
   * Callback fired when an option is changed
   */
  onChange?: (newOptionIndex: number, ...args: any[]) => void;
  /**
   * Accepts an array of options that are then used within the dropdown
   */
  options: OptionType[];
  /**
   * Shows the currently selected value within the dropdown
   */
  selected: number;
  /**
   * Updates the selected value within the dropdown
   */
  setSelected: Dispatch<SetStateAction<number>>;
  /**
   * The size of Dropdown to use.
   */
  size?: Size;
  /**
  /**
   * The variant of Dropdown to use.
   */
  variant?: Variant;
};

const Dropdown = ({
  classFieldset,
  title,
  options,
  onChange,
  selected = 0,
  setSelected,
  size = Size.small,
  variant = Variant.outlined,
}: DropdownProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLFieldSetElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  useEscapeHandlers(isOpen, setIsOpen, wrapperRef, focusRef);

  const handleChange = useCallback(
    (idx: number) => {
      if (onChange) {
        onChange(idx);
      }
    },
    [onChange]
  );

  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  const offset = (): number => {
    if (buttonRef.current) {
      return buttonRef.current.offsetHeight;
    }

    return 53;
  };

  const selectedLabel = `${options[selected].label} ${
    options[selected].labelAdditional
      ? ` (${options[selected].labelAdditional})`
      : ''
  }`;

  const buttonStyles = cx(styles.dropdownButton, styles[variant], styles[size]);
  const containerStyles = cx(styles.container, styles[size]);
  const fieldsetStyles = cx(styles.fieldset, classFieldset);

  return (
    <div className={styles.wrapper} ref={wrapperRef} data-test-id='dropdown'>
      <button
        data-test-id='dropdown-button'
        className={buttonStyles}
        onClick={handleOpen}
        ref={buttonRef}
      >
        <div className={containerStyles}>
          {title && <span className={styles.title}>{title}</span>}
          <div className={styles.content}>
            <span>{selectedLabel}</span>
            {isOpen ? (
              <Icon
                iconName='arrows.up'
                iconColor='contentLink'
                className={styles.icon}
              />
            ) : (
              <Icon
                iconName='arrows.down'
                iconColor='contentLink'
                className={styles.icon}
              />
            )}
          </div>
        </div>
      </button>
      {isOpen && (
        <fieldset
          className={fieldsetStyles}
          ref={focusRef}
          data-test-id='dropdown-content'
          style={{
            top: offset() + 8,
          }}
        >
          {options.map((option, idx) => {
            return (
              <RadioItem
                key={option.label}
                label={option.label}
                labelAdditional={option.labelAdditional}
                selectedOption={options[selected]}
                onChange={() => handleChange(idx)}
                setDropdownOpen={handleOpen}
                setSelectedOption={() => setSelected(idx)}
                dataTestId={kebabCase(
                  option.labelAdditional
                    ? `${option.label} ${option.labelAdditional}`
                    : `${option.label}`
                )}
              />
            );
          })}
        </fieldset>
      )}
    </div>
  );
};

export default Dropdown;
